import { addFieldVersion, applyFieldVersion, removeFieldVersion, writeFieldVersion } from './versions.js';
import { answerToleranceMode, answerHalfWidths } from './answer-tolerance.js';
import { matchesValueTrigger as matchesValueTriggerShared, parseValueTrigger as parseValueTriggerShared, triggerToBoundedInterval as triggerToBoundedIntervalShared, } from './value-trigger.js';
/**
 * Slider's three adapters onto the shared trigger vocabulary.
 *
 * A slider trigger carries a HANDLE SCOPE that no other block has: `a:gt:80` means "handle A is
 * above 80", so the string has to be split into handle and condition before the condition can be
 * read. Stripping it once here is what lets the shared reader stay ignorant of handles.
 *
 * This difference is also the reason the census's first pass got slider wrong. The measuring script
 * looked for a function's body brace and found the brace in `parseValueTrigger`'s RETURN TYPE
 * instead, so it compared signatures and reported five identical copies where there were three. The
 * same flaw then drove the deletion, which is how a genuinely different function came to be deleted
 * as a duplicate. Four slider tests caught it immediately; the lesson is that a census is only as
 * good as its measurement, and "identical" deserves a spot-check against a real behavioural test.
 *
 * Slider passes no domain bounds: its min/max are AUTHORED, so a one-sided trigger runs to infinity
 * here and the authored range narrows it later. Angle and gps, whose domains are intrinsic, pass
 * theirs.
 */
function parseValueTrigger(trigger) {
    return parseValueTriggerShared(parseSliderHandleTrigger(trigger).condition);
}
function matchesValueTrigger(trigger, value) {
    return matchesValueTriggerShared(parseSliderHandleTrigger(trigger).condition, value);
}
function triggerToBoundedInterval(trigger) {
    return triggerToBoundedIntervalShared(parseSliderHandleTrigger(trigger).condition);
}
import { EPS } from './epsilon.js';
import { coverageSpansDomain, isIntervalFullyCovered, mergeCoverageIntervals, rangesOverlap } from './interval-coverage.js';
import { resolveScenarioValueSetup } from './scenario.js';
import { optionalOutcome, participationOutcome, SET_ANSWER_TRIGGER } from './grading-mode.js';
/** Absolute last-resort backstop against a runaway tick array locking up the browser — see
 * computeTickPositions. Far above any UI-readability threshold; those are enforced separately. */
const MAX_TICKS_HARD_CAP = 5000;
/** True when `value` lies inside `iv`, honouring each end's own open/closed boundary. EPS is applied
 * only to the inclusive comparisons: an exclusive boundary is an exact-equality question, and
 * widening it by an epsilon would re-admit the very point it exists to exclude. */
function containsValue(iv, value) {
    const aboveLo = iv.loInclusive ? value >= iv.lo - EPS(iv.lo) : value > iv.lo;
    const belowHi = iv.hiInclusive ? value <= iv.hi + EPS(iv.hi) : value < iv.hi;
    return aboveLo && belowHi;
}
/**
 * The accepted range(s) implied by the canonical condition (one-sided conditions use ±Infinity).
 * Multiple exact/between answers are OR'd together. Ported from numerical.ts's own version — same
 * shape/behavior, slider field names.
 */
export function computeSliderAcceptedRanges(config) {
    const { conditionType, answers, betweenAnswers, threshold } = config;
    if (conditionType === 'exact') {
        return answers.filter(a => Number.isFinite(a.value)).map(a => {
            const { below, above } = answerHalfWidths(a, config);
            const mode = answerToleranceMode(a, config, 'absolute');
            /**
             * `Math.abs`, because a tolerance is a DISTANCE and a percentage of a negative answer is
             * not. `log(10)-2` is -1, and 1% of -1 is -0.01, so the band came out
             * `lo: -0.99, hi: -1.01` — inverted, and `isWithinAcceptedRange` then rejected every value
             * including the exact answer. A teacher with a negative answer and percentage error had a
             * block that could not be answered correctly at all.
             *
             * Absolute mode is clamped the same way: a negative distance is meaningless whatever typed
             * it, and letting one through produces the same inverted interval.
             */
            const loErr = Math.abs(mode === 'absolute' ? below : a.value * (below / 100));
            const hiErr = Math.abs(mode === 'absolute' ? above : a.value * (above / 100));
            // A tolerance band includes its own endpoints — "5 ± 1" accepts exactly 4 and exactly 6.
            return { lo: a.value - loErr, hi: a.value + hiErr, loInclusive: true, hiInclusive: true };
        });
    }
    if (conditionType === 'between') {
        return betweenAnswers.filter(a => Number.isFinite(a.low) && Number.isFinite(a.high))
            .map(a => ({ lo: a.low, hi: a.high, loInclusive: true, hiInclusive: true }));
    }
    if (conditionType === 'lt' || conditionType === 'lte') {
        return [{ lo: -Infinity, hi: threshold, loInclusive: true, hiInclusive: conditionType === 'lte' }];
    }
    return [{ lo: threshold, hi: Infinity, loInclusive: conditionType === 'gte', hiInclusive: true }];
}
/** Is this value within the accepted condition? Handles lt/gt's exclusive boundary explicitly. */
export function isSliderWithinAcceptedRange(value, config) {
    if (config.conditionType === 'lt')
        return value < config.threshold;
    if (config.conditionType === 'lte')
        return value <= config.threshold;
    if (config.conditionType === 'gt')
        return value > config.threshold;
    if (config.conditionType === 'gte')
        return value >= config.threshold;
    return computeSliderAcceptedRanges(config).some(r => containsValue(r, value));
}
/** Rounds a raw dragged value to the nearest reachable step position. */
export function quantizeToStep(value, min, stepSize) {
    if (!Number.isFinite(stepSize) || stepSize <= 0)
        return value;
    return min + Math.round((value - min) / stepSize) * stepSize;
}
/**
 * Whether any reachable stepped value (min, min+step, min+2*step, ...) falls within [lo, hi].
 *
 * It used to round `from` to the NEAREST step and test that one value. Nearest rounds DOWN as often
 * as up, and a step below `lo` is outside the band by construction, so the answer was "no reachable
 * step" for any band whose lower edge happened to round down — however many steps actually sat
 * inside it. On a 0-100 slider with step 10, the band 61-79 contains 70 and reported unreachable;
 * so did 12-28, which contains 20.
 *
 * That is not a cosmetic miss. It drives the "correct answer is unreachable" warning, and the
 * editor's answer bar responds to it by printing `quantizeToStep(value)` instead of the answer —
 * so an authored 66.5 ± 5% was displayed as "70", a number the evaluator does not accept.
 *
 * The first step AT OR ABOVE `lo` is the only candidate worth testing: if it is past `hi` there is
 * nothing in between, and if it is not, it is itself the witness. Same ceil-with-epsilon shape as
 * `bandAdmitsSigFigs` in numerical.ts, which answers the same question about a different lattice.
 */
export function hasReachableStepInRange(lo, hi, min, max, stepSize) {
    if (!Number.isFinite(stepSize) || stepSize <= 0)
        return true;
    const from = Math.max(lo, min);
    const to = Math.min(hi, max);
    if (from > to)
        return false;
    const first = min + Math.ceil((from - min) / stepSize - EPS(from)) * stepSize;
    return first <= to + EPS(to);
}
/**
 * The log-scale twin of `hasReachableStepInRange`: can a student land inside [lo, hi] when the
 * positions are decade-relative rather than evenly spaced?
 *
 * Needed because the linear version answers a different question with the same confidence. A log
 * slider from 1 to 100 at 9 steps per decade stops on 1,2,...,9,10,20,...,100 — not on `stepSize`
 * at all, which log mode does not use. Asking the linear function about a log slider gave a verdict
 * drawn from a lattice the block does not have; it happened to be right on the case that exposed
 * it, which is the worst way for a wrong method to behave.
 *
 * Positions increase monotonically, so the first one at or above `lo` is the only candidate: if it
 * is past `hi` nothing lies between, and if it is not, it is the witness.
 */
export function hasReachableLogStepInRange(lo, hi, min, max, stepsPerDecade) {
    if (!Number.isFinite(stepsPerDecade) || stepsPerDecade <= 0 || min <= 0)
        return true;
    const from = Math.max(lo, min);
    const to = Math.min(hi, max);
    if (from > to || from <= 0)
        return false;
    const mantissaStep = 9 / stepsPerDecade;
    const decadeBase = Math.pow(10, Math.floor(Math.log10(from)));
    const mantissa = from / decadeBase;
    // First quantized mantissa at or above this one. A mantissa that rolls past 10 is simply the next
    // decade's 1, which is the correct numeric value with no special case — same reasoning as
    // `logStepValue`'s carry.
    const firstMantissa = 1 + Math.ceil((mantissa - 1) / mantissaStep - EPS(mantissa)) * mantissaStep;
    return firstMantissa * decadeBase <= to + EPS(to);
}
/** Log-space analogue of quantizeToStep — "decade-relative" (significant-figure) stepping, not
 * geometric/log-uniform stepping. Within each decade [10^k, 10^(k+1)), reachable values are
 * whole-mantissa-style points `10^k, 10^k*(1+Δ), 10^k*(1+2Δ), ..., 10^(k+1)` where Δ = 9/N for N
 * "steps per decade" — N=9 (the default) hits whole-number mantissas (1,2,3,...,9,10 within the
 * 1-10 decade, 10,20,...,100 within the 10-100 decade, and so on); a larger N subdivides further
 * (N=90 hits tenths, e.g. 1.0, 1.1, 1.2, ... within 1-10). This intentionally does NOT produce
 * uniform visual spacing on the log axis (1→2 spans more slider travel than 9→10) — that unevenness
 * is the expected, recognizable look of a real log/slide-rule scale, not a bug. (Superseded the
 * original geometric/log-uniform model — `value * 10^(1/N)` steps — which produced mathematically
 * even log-spacing but non-round numbers except at decade boundaries; see
 * project_slider_block_scoping memory for the full before/after discussion.) */
export function quantizeLogStep(value, min, stepsPerDecade) {
    if (!Number.isFinite(stepsPerDecade) || stepsPerDecade <= 0 || min <= 0 || value <= 0)
        return value;
    const decade = Math.floor(Math.log10(value));
    const decadeBase = Math.pow(10, decade);
    const mantissaStep = 9 / stepsPerDecade;
    const mantissa = value / decadeBase;
    const quantizedMantissa = 1 + Math.round((mantissa - 1) / mantissaStep) * mantissaStep;
    return quantizedMantissa * decadeBase;
}
/** Decade-relative log stepping for keyboard interaction — moves by one mantissa-step within the
 * *current* decade (see quantizeLogStep), naturally carrying into the adjacent decade when the
 * mantissa crosses 1 or 10 (e.g. stepping up from mantissa 9 with a whole-number step lands on
 * mantissa 10, i.e. exactly the start of the next decade — no special-casing needed since that's
 * just the correct numeric value either way). `steps` may be negative (step down) or ±10 (page). */
export function logStepValue(from, min, stepsPerDecade, steps) {
    if (!Number.isFinite(stepsPerDecade) || stepsPerDecade <= 0 || min <= 0 || from <= 0)
        return from;
    const decade = Math.floor(Math.log10(from));
    const decadeBase = Math.pow(10, decade);
    const mantissaStep = 9 / stepsPerDecade;
    const mantissa = from / decadeBase;
    return (mantissa + steps * mantissaStep) * decadeBase;
}
/** Maps a value to its fractional position [0, 1] along [min, max], honoring scaleMode —
 * shared by the drag primitive and any preview/collapsed-view rendering so log-space mapping
 * never gets reimplemented ad hoc in more than one place. */
export function valueToFraction(value, min, max, scaleMode) {
    if (scaleMode === 'log') {
        if (min <= 0 || max <= 0 || max <= min)
            return 0;
        const clamped = Math.min(max, Math.max(min, value));
        return (Math.log10(clamped) - Math.log10(min)) / (Math.log10(max) - Math.log10(min));
    }
    if (max <= min)
        return 0;
    const clamped = Math.min(max, Math.max(min, value));
    return (clamped - min) / (max - min);
}
/** Inverse of valueToFraction — maps a fractional position [0, 1] back to a value. */
export function fractionToValue(fraction, min, max, scaleMode) {
    const f = Math.min(1, Math.max(0, fraction));
    if (scaleMode === 'log') {
        if (min <= 0 || max <= 0)
            return min;
        const logMin = Math.log10(min);
        const logMax = Math.log10(max);
        return Math.pow(10, logMin + f * (logMax - logMin));
    }
    return min + f * (max - min);
}
/** How many ticks a given interval would produce across [min, max] — shared by the UI's
 * readability-warning validation and computeTickPositions' own hard safety cap, so the two never
 * disagree on what "count" means. Returns 0 for a non-positive/invalid interval or range. */
export function sliderTickCount(min, max, interval) {
    if (!Number.isFinite(interval) || interval <= 0 || max <= min)
        return 0;
    return Math.round((max - min) / interval);
}
/**
 * How many major ticks may carry their own numeric value before the text is a smear.
 *
 * A readability threshold, far below `MAX_TICKS_HARD_CAP` — that one stops the browser hanging,
 * this one stops 0.1-interval majors on a 0-100 scale stacking unreadable numbers on top of each
 * other, which is what prompted it.
 */
export const MAX_READABLE_MAJOR_TICKS = 40;
/**
 * Whether the auto-rendered values on major ticks would actually appear.
 *
 * The teacher's toggle is necessary and not sufficient: past `MAX_READABLE_MAJOR_TICKS` the labels
 * are suppressed however the toggle is set (the marks themselves still draw — this concerns only
 * the numbers beside them).
 *
 * Lives here rather than in the slider's own component because a Flow effect can flip
 * `showMajorTickValues`, and an effect that applies and changes nothing visible is the exact
 * failure mode the effect catalogue exists to refuse. The picker greys that row with a reason,
 * which it can only do by asking the same question the renderer asks.
 */
export function shouldShowMajorTickValues(showMajorTickValues, scaleMode, min, max, majorTickInterval) {
    if (!showMajorTickValues)
        return false;
    if (scaleMode === 'log')
        return true; // log majors are always whole decades — never dense
    return sliderTickCount(min, max, majorTickInterval) <= MAX_READABLE_MAJOR_TICKS;
}
/**
 * Tick mark positions (values, not fractions), split into major/minor so the track can render
 * them with different visual weight. Linear mode: majors are spaced every `majorTickInterval`
 * from min, minors subdivide each major-to-major gap into `minorTicksPerMajor + 1` equal
 * segments — both teacher-configurable, since tick *frequency* is independent of the step size
 * (a coarse step can still want fine-grained visual ticks, or vice versa). Log mode keeps its
 * fixed decade-majors + 2-9-minors scheme (slide-rule style) — not configurable, since a fixed
 * additive interval doesn't map meaningfully onto a logarithmic axis.
 */
export function computeTickPositions(min, max, scaleMode, majorTickInterval, minorTickInterval) {
    if (scaleMode === 'log') {
        const major = [];
        const minor = [];
        if (min <= 0 || max <= min)
            return { major, minor };
        const startDecade = Math.floor(Math.log10(min));
        const endDecade = Math.ceil(Math.log10(max));
        for (let d = startDecade; d <= endDecade; d++) {
            const decadeValue = Math.pow(10, d);
            if (decadeValue >= min - EPS(min) && decadeValue <= max + EPS(max))
                major.push(decadeValue);
            for (let m = 2; m <= 9; m++) {
                const v = m * decadeValue;
                if (v >= min - EPS(min) && v <= max + EPS(max))
                    minor.push(v);
            }
        }
        return { major, minor };
    }
    const major = [];
    const minor = [];
    if (!Number.isFinite(majorTickInterval) || majorTickInterval <= 0 || max <= min)
        return { major, minor };
    // Hard safety cap — NOT a limit on the interval value itself (a genuinely fine-grained scale,
    // e.g. 0-1 with ticks every 0.0001, is a legitimate ask), but on the resulting tick *count*.
    // Without this, a tiny interval relative to a huge range (e.g. 0.0000001 on a 0-100 scale)
    // computes a billion-element array and locks up the browser. Deliberately generous — the UI
    // layer applies a much tighter, readability-motivated warning threshold well below this; this
    // is purely the last-resort backstop so the function itself can never hang regardless of caller.
    const majorCount = sliderTickCount(min, max, majorTickInterval);
    if (majorCount > MAX_TICKS_HARD_CAP)
        return { major, minor };
    for (let i = 0; i <= majorCount; i++)
        major.push(min + i * majorTickInterval);
    // Minor ticks are every multiple of minorTickInterval that ISN'T already a major position —
    // e.g. major=5/minor=1 on a 0-10 scale draws minors at 1,2,3,4,6,7,8,9, not just the midpoint
    // of each major gap (a count-based "N minors between majors" model doesn't match how teachers
    // actually think about tick spacing).
    if (Number.isFinite(minorTickInterval) && minorTickInterval > 0) {
        const minorCount = sliderTickCount(min, max, minorTickInterval);
        if (minorCount > MAX_TICKS_HARD_CAP)
            return { major, minor: [] };
        for (let i = 0; i <= minorCount; i++) {
            const v = min + i * minorTickInterval;
            const isMajor = major.some(m => Math.abs(m - v) < EPS(v));
            if (!isMajor)
                minor.push(v);
        }
    }
    return { major, minor };
}
/** Which handle a targeted trigger is about. Mirrors FITB's `buildFitbWordTrigger(blankId, wordId)`:
 * one feedback set for the block, with each targeted state naming the input it belongs to. */
export function buildSliderHandleTrigger(handle, condition) {
    return `${handle}:${condition}`;
}
/** Splits the handle prefix off a trigger. An unprefixed trigger is handle A — every slider
 * authored before dual handle had one input, and its states are still about that input. */
export function parseSliderHandleTrigger(trigger) {
    const [head, ...rest] = trigger.split(':');
    if ((head === 'a' || head === 'b') && rest.length > 0)
        return { handle: head, condition: rest.join(':') };
    return { handle: 'a', condition: trigger };
}
/**
 * Whether Correct is unreachable: some handle accepts nothing at all.
 *
 * An empty range list is an answer key with no answer in it, and a range unbounded at BOTH ends is
 * the same thing wearing the opposite disguise — it comes from an unparseable threshold, where
 * `lo` and `hi` both resolve to NaN rather than to a real span. Neither can be satisfied, and with
 * two handles Correct is a conjunction, so one such handle settles it for the block.
 */
export function isSliderCorrectUnreachable(perHandleRanges) {
    return perHandleRanges.some(ranges => ranges.length === 0 || ranges.some(x => !Number.isFinite(x.lo) && !Number.isFinite(x.hi)));
}
/** Whether Default Incorrect (for the value dimension) is genuinely unreachable. */
/** Slider's own reading of a trigger as coverage: the three span-shaped types contribute, `exact`
 *  does not (a point never closes a gap). Passed to the shared merge so the interval maths is
 *  shared while the trigger vocabulary stays here. */
function sliderTriggerSpan(trigger) {
    if (parseValueTrigger(trigger)?.type === 'exact')
        return null;
    return triggerToBoundedInterval(trigger);
}
/**
 * Pairs of authored targeted triggers whose bands overlap each other — likely unintentional.
 *
 * Slider was the one value block without this. It had a check named `feedback-overlap`, but all
 * that did was count IDENTICAL trigger strings: two cards both reading `a:range:10:30` were
 * reported, and `a:range:10:30` beside `a:range:20:40` were not, which is the case a teacher
 * actually creates. Compass, GPS, Rotation, Tilt, Numerical and Ordering each have a real one.
 *
 * GROUPED BY HANDLE, which is what makes this slider's rather than a copy of numerical's. A
 * trigger carries an `a:` or `b:` prefix, and the two handles are separate number lines: Slider 1
 * catching 10-30 while Slider 2 catches 20-40 is not a collision, it is an ordinary pair of
 * conditions about two different inputs, and reporting it would train the teacher to ignore the
 * warning. Only same-handle pairs can both describe the same position.
 *
 * `exact` triggers are excluded by `sliderTriggerSpan`, mirroring the other value blocks: a
 * specific callout nested inside a broader band is a common deliberate pattern, not a mistake.
 */
export function findSliderOverlappingFeedbackPairs(feedbackTriggers) {
    const byHandle = new Map();
    for (const trigger of feedbackTriggers) {
        const span = sliderTriggerSpan(trigger);
        if (!span)
            continue;
        const { handle } = parseSliderHandleTrigger(trigger);
        const list = byHandle.get(handle) ?? [];
        list.push({ trigger, span });
        byHandle.set(handle, list);
    }
    const pairs = [];
    for (const list of byHandle.values()) {
        for (let i = 0; i < list.length; i++) {
            for (let j = i + 1; j < list.length; j++) {
                const a = list[i], b = list[j];
                if (rangesOverlap(a.span, b.span))
                    pairs.push([a.trigger, b.trigger]);
            }
        }
    }
    return pairs;
}
export function isSliderValueDefaultIncorrectUnreachable(acceptedRanges, feedbackTriggers) {
    const merged = mergeCoverageIntervals(acceptedRanges, feedbackTriggers, sliderTriggerSpan);
    return coverageSpansDomain(merged, -Infinity, Infinity);
}
/** The value-dimension gaps that actually reach Default Incorrect. */
export function computeSliderDefaultIncorrectGaps(acceptedRanges, feedbackTriggers) {
    const merged = mergeCoverageIntervals(acceptedRanges, feedbackTriggers, sliderTriggerSpan);
    const gaps = [];
    if (merged.length === 0)
        return [{ lo: -Infinity, hi: Infinity, loInclusive: true, hiInclusive: true }];
    const first = merged[0];
    // A gap's boundary is the mirror of the span beside it: coverage that stops OPEN at 5 leaves a gap
    // that is CLOSED at 5, because 5 itself is uncovered. That is how a single-point gap arises, and
    // representing it is the difference between "Default Incorrect is unreachable" and "it is reached
    // by exactly one value".
    if (first.lo !== -Infinity) {
        gaps.push({ lo: -Infinity, hi: first.lo, loInclusive: true, hiInclusive: !first.loInclusive });
    }
    for (let i = 1; i < merged.length; i++) {
        const prev = merged[i - 1];
        const cur = merged[i];
        if (cur.lo > prev.hi || (cur.lo === prev.hi && !prev.hiInclusive && !cur.loInclusive)) {
            gaps.push({ lo: prev.hi, hi: cur.lo, loInclusive: !prev.hiInclusive, hiInclusive: !cur.loInclusive });
        }
    }
    const last = merged[merged.length - 1];
    if (last.hi !== Infinity) {
        gaps.push({ lo: last.hi, hi: Infinity, loInclusive: !last.hiInclusive, hiInclusive: true });
    }
    return gaps;
}
/** Pairs of currently-authored value feedback whose ranges overlap each other. */
export function findOverlappingSliderFeedbackPairs(feedbackTriggers) {
    const parsed = feedbackTriggers
        .map(trigger => {
        const p = parseValueTrigger(trigger);
        if (!p || p.type === 'exact')
            return null;
        if (p.type === 'lt')
            return { trigger, lo: -Infinity, hi: p.n1, loInclusive: true, hiInclusive: false };
        if (p.type === 'lte')
            return { trigger, lo: -Infinity, hi: p.n1, loInclusive: true, hiInclusive: true };
        if (p.type === 'gt')
            return { trigger, lo: p.n1, hi: Infinity, loInclusive: false, hiInclusive: true };
        if (p.type === 'gte')
            return { trigger, lo: p.n1, hi: Infinity, loInclusive: true, hiInclusive: true };
        return { trigger, lo: Math.min(p.n1, p.n2), hi: Math.max(p.n1, p.n2), loInclusive: true, hiInclusive: true };
    })
        .filter((x) => x !== null);
    const pairs = [];
    for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
            const a = parsed[i], b = parsed[j];
            if (rangesOverlap(a, b))
                pairs.push([a.trigger, b.trigger]);
        }
    }
    return pairs;
}
/** Whether a value-feedback state's entire condition now falls inside the accepted range. */
export function isSliderValueFeedbackFullySubsumed(trigger, acceptedRanges) {
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    return isIntervalFullyCovered(target, acceptedRanges);
}
/** Whether a value-feedback trigger's target interval intersects the accepted range at all. */
export function doesSliderValueFeedbackOverlapAccepted(trigger, acceptedRanges) {
    const parsed = parseValueTrigger(trigger);
    if (!parsed || parsed.type === 'exact')
        return false;
    const target = triggerToBoundedInterval(trigger);
    if (!target)
        return false;
    // Both sides' boundaries now count. Previously the accepted range was assumed closed, so
    // Correct = "<= 10" and a trigger of "> 10" — perfect complements sharing no point — were
    // reported as overlapping.
    return acceptedRanges.some(r => {
        if (target.hi < r.lo || r.hi < target.lo)
            return false;
        if (target.hi === r.lo)
            return target.hiInclusive && r.loInclusive;
        if (r.hi === target.lo)
            return r.hiInclusive && target.loInclusive;
        return true;
    });
}
/** Exact-value feedback triggers whose point falls inside the given Default Incorrect gap. */
export function exactSliderFeedbackPointsInGap(gap, feedbackTriggers) {
    const points = [];
    for (const trigger of feedbackTriggers) {
        const parsed = parseValueTrigger(trigger);
        if (!parsed || parsed.type !== 'exact')
            continue;
        if (parsed.n1 >= gap.lo && parsed.n1 <= gap.hi)
            points.push(parsed.n1);
    }
    return [...new Set(points)].sort((a, b) => a - b);
}
function handleRangeConfig(d, handle) {
    if (handle === 'a' || !d.handleB) {
        const { conditionType, answers, betweenAnswers, toleranceMode, toleranceSymmetry, threshold } = d;
        return { conditionType, answers, betweenAnswers, toleranceMode, toleranceSymmetry, threshold };
    }
    const { conditionType, answers, betweenAnswers, toleranceMode, toleranceSymmetry, threshold } = d.handleB;
    return { conditionType, answers, betweenAnswers, toleranceMode, toleranceSymmetry, threshold };
}
/** Whether one handle's submitted position satisfies its own accepted condition. */
export function isSliderHandleCorrect(answer, blockData, handle) {
    return isSliderWithinAcceptedRange(answer.value, handleRangeConfig(blockData, handle));
}
/**
 * Returns the feedback states matching a student's submission.
 *
 * A dual-handle slider is TWO INPUTS WITH ONE VERDICT, exactly like FITB's blanks — see
 * fitb.ts's `matches.every(m => m.word?.role === 'correct')`. `correct` fires only when every
 * handle in play is within its own accepted range; a single handle out of range means the block is
 * not correct, however good the other one is. That is why there is one feedback set rather than two:
 * a block has one outcome, and Flow routes on that outcome.
 *
 * Targeted states name their handle (`a:exact:5`, `b:range:10:20`), so an out-of-range submission
 * fans out to every targeted state matching ANY handle's position — the same parallel fan-out FITB
 * does across blanks — falling back to `incorrect` when none match.
 *
 * Evaluation sequence (same shape as evaluateNumerical, minus the unit-check and sig-figs-gate
 * branches — neither concept applies to slider):
 * 1. optional mode → any_response, touched or not — no correctness check at all for this mode
 *    (see positiveTriggerForMode in use-input-block-feedback.ts).
 * 2. Any handle untouched (graded/survey) → unanswered. Partial completion is not a separate rung:
 *    a slider always HAS a position, so "touched" is the only completion signal there is.
 * 3. survey mode → any_answer (any touched position counts as participation).
 * 4. graded mode: every handle in range → `correct`; otherwise the targeted fan-out, else
 *    `incorrect`.
 */
/**
 * This block's answer setup for the scenario in force — the value family's shared rule, with
 * only this block's own field list and fail-closed shape supplied.
 *
 * Empty answer lists are what "no row" means: nothing can be correct, which is the same
 * fail-closed choice the choice family makes by going all-neutral.
 */
export function resolveSliderScenario(d, scenarioId) {
    return resolveScenarioValueSetup(d, scenarioId, SLIDER_VERSION_KEYS, { answers: [], betweenAnswers: [] });
}
export function evaluateSlider(answer, rawBlockData, answerB, 
/** The leader's variant in force — see `resolveScenarioValueSetup`. */
scenarioId) {
    const blockData = resolveSliderScenario(rawBlockData, scenarioId);
    const { gradingMode, feedback, dualHandle } = blockData;
    const dual = dualHandle && !!blockData.handleB && !!answerB;
    const submissions = dual
        ? [{ handle: 'a', answer }, { handle: 'b', answer: answerB }]
        : [{ handle: 'a', answer }];
    const find = (trigger) => feedback.states.find(s => s.type === 'default' && s.trigger === trigger);
    /** The targeted states these handle positions fire. ONE matcher, used by every grading mode —
     *  see `participationOutcome`. */
    const targetedForHandles = () => feedback.states.filter(st => {
        if (st.type !== 'targeted')
            return false;
        /*
         * SET ANSWER IS JOINT, and is tested before the handle is parsed.
         *
         * Every other targeted trigger carries a condition the teacher wrote about ONE handle, so it
         * is matched against that handle alone and several can fire together. Set Answer carries no
         * condition at all: it points at Correct, and Correct on a dual slider is the conjunction
         * below. Matching it per handle would invent a predicate this block does not have — "handle A
         * is in its band" — which fires for a student who put B somewhere else, and would report
         * that they had given the authored answer when they had not.
         *
         * Ahead of `parseSliderHandleTrigger` because that function resolves a trigger with no `a:`
         * or `b:` prefix to handle A rather than rejecting it. A joint trigger would be silently
         * bound to the first handle, then fail to parse as a value condition, and simply never fire
         * with nothing on screen to say why.
         */
        if (st.trigger === SET_ANSWER_TRIGGER) {
            return submissions.every(sub => isSliderHandleCorrect(sub.answer, blockData, sub.handle));
        }
        const { handle } = parseSliderHandleTrigger(st.trigger);
        const sub = submissions.find(x => x.handle === handle);
        return !!sub && matchesValueTrigger(st.trigger, sub.answer.value);
    });
    // Targeted value states fire here too. Whether one CAN fire is analysed against the accepted
    // band, and outside graded there is no band in force — correct is never checked, so nothing is
    // subsumed by it. The editor's own analysis is mode-aware to match.
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(feedback.states);
    }
    if (submissions.some(sub => !sub.answer.touched)) {
        const unanswered = find('unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(feedback.states, 'survey', targetedForHandles());
    }
    if (submissions.every(sub => isSliderHandleCorrect(sub.answer, blockData, sub.handle))) {
        const correct = find('correct');
        return correct ? [correct] : [];
    }
    const matched = targetedForHandles();
    if (matched.length > 0)
        return matched;
    const incorrect = find('incorrect');
    return incorrect ? [incorrect] : [];
}
/**
 * The fields a Slider version owns — §3.2 of `docs/question-versions.md`.
 *
 * `handleB` travels whole: a second handle carries its own accepted values, and an answer setup
 * split across two versions would grade the right handle against the wrong question.
 */
export const SLIDER_VERSION_KEYS = ['conditionType', 'answers', 'toleranceMode', 'toleranceSymmetry', 'betweenAnswers', 'threshold', 'handleB'];
export function applySliderVersion(data, versionId) {
    return applyFieldVersion(data, versionId, SLIDER_VERSION_KEYS);
}
export function writeSliderVersion(current, versionId, next) {
    return writeFieldVersion(current, versionId, next, SLIDER_VERSION_KEYS);
}
export function addSliderVersion(data, fromVersionId, newId) {
    return addFieldVersion(data, fromVersionId, newId, SLIDER_VERSION_KEYS);
}
export function removeSliderVersion(data, versionId) {
    return removeFieldVersion(data, versionId, SLIDER_VERSION_KEYS);
}
//# sourceMappingURL=slider.js.map